import { useState, useEffect } from 'react';
import { FileText, Printer} from 'lucide-react';
import apiService from '../config';
import { useLanguage } from "../StoreContext/LanguageContext";
import { useSignature } from '../DigitalSignature/UseSignatue';
import SignaturePad from '../DigitalSignature/SignaturePad';

const Section21_1 = ({ setSubComponent }) => {
  const { language, translations } = useLanguage();
  const { signDocument, selectedSignDocument } = useSignature();
  const [showPreview, setShowPreview] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [currentProposalId, setCurrentProposalId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [defaultMainContent, setDefaultMainContent] = useState('');
  const [proposalData, setProposalData] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [pdfForSigning, setPdfForSigning] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);

  // Editable form data
  const [formData, setFormData] = useState({
    caseNumber: "",
    village: "",
    taluka: "",
    district: "",
    fileNumber: "",
    section_21_1_date: "",
    area: "",
    section_21_1_notification: "",
    lastDate: "",
    landOwners: [],
    header: "",
    officename: "",
    branchname: "",
    sectioncontent: "",
    email: "",
    contactNo: "",
    section_21_1_submission_date: "",
    section_21_1_representative_address: ""
  });

  const [loadingusersLAO, setLoadingusersLAO] = useState(false);
  const [usersLAO, setusersLAO] = useState([]);
  const laoName = usersLAO?.[0]?.full_name || 'N/A';
  const laoDesi = usersLAO?.[0]?.designation || 'N/A';

  const ROLES = {
    COLLECTOR: 1,
    LAO: 3,
    SAMANVAY: 6,
    PROJECT_INCHARGE: 18,
    DATA_ENTRY: 14
  };

  const userData = JSON.parse(localStorage.getItem('userData'));
  const userRole = parseInt(userData?.data?.user_role || userData?.user_role, 10);

  // Fetch projectId
  useEffect(() => {
    const getProjectInfo = () => {
      const currentProjectData = localStorage.getItem('currentProjectData');
      if (currentProjectData) {
        try {
          const projectData = JSON.parse(currentProjectData);
          return {
            id: projectData.project_id || projectData.id,
            name: projectData.project_name || projectData.name
          };
        } catch (e) {
          console.error('Error parsing current project data (currentProjectData):', e);
        }
      }

      const selectedProject = localStorage.getItem('selectedProject');
      if (selectedProject) {
        try {
          const project = JSON.parse(selectedProject);
          return {
            id: project.project_id || project.id,
            name: project.project_name || project.name
          };
        } catch (e) {
          console.error('Error parsing selected project (selectedProject):', e);
        }
      }

      const userData = localStorage.getItem('userData');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          const projectId = user.data?.project_id || user.project_id;
          const projectName = user.data?.project_name || user.project_name || 'Unknown Project';
          return {
            id: projectId,
            name: projectName
          };
        } catch (e) {
          console.error('Error parsing user data (userData):', e);
        }
      }
      return { id: null, name: null };
    };

    const projectInfo = getProjectInfo();
    setCurrentProjectId(projectInfo.id);
  }, []);

  // Fetch proposalId
  useEffect(() => {
    const getProposalInfo = () => {
      const currentProposal = JSON.parse(localStorage.getItem('userData') || '{}');
      if (currentProposal) {
        try {
          return {
            proposalId: currentProposal?.data?.default_proposal ||
              currentProposal?.data?.proposal_id ||
              currentProposal?.default_proposal ||
              currentProposal?.proposal_id ||
              null
          };
        } catch (e) {
          console.error('Error parsing current project data (getProposalInfo):', e);
        }
      }
      return { proposalId: null };
    };

    const proposalInfo = getProposalInfo();
    setCurrentProposalId(proposalInfo.proposalId);
  }, []);

  // Fetch data from API when projectId and proposalId are available
  useEffect(() => {
    if (!currentProjectId || !currentProposalId) return;

    const fetchAll = async () => {
      setLoadingRecords(true);
      try {
        // 1. Proposal details
        const propRes = await apiService.getProposalById(currentProjectId, currentProposalId);
        setProposalData(propRes.data || {});

        // 2. Land owners
        const ownerRes = await apiService.get("getAllOwner", {
          projectId: currentProjectId,
          proposalId: currentProposalId,
          isActive: 1
        });

        // 3. Check if already signed PDF exists
        const sectionRes = await apiService.getSectionFile(
          currentProjectId,
          currentProposalId,
          "section21"
        );

        if (sectionRes?.isSignAvailable && sectionRes.data?.[0]?.file_path) {
          setPreviewUrl(sectionRes.data[0].file_path);
        }

        const landData = ownerRes.landAcquisition;

        // Default main content if not present in API
        const content = `यादवारे नोटीस देण्यात येते की, भूसंपादन प्रकरण ${propRes.data.section_11_bhusampadan_no} नुसार मौजे ${propRes.data.village_name} या गावामध्ये सोबतच्या यादीमध्ये नमुद असलेली तसेच अलीकडे चिन्हांकित केलेली व मोजलेली सुमारे ${propRes.data.acquired_area || ""} भूमी, तिच्यावर  कोणत्याही इमारती बांधकाम व झाडे असल्यास त्यासह समुचित शासन म्हणून जिल्हाधिकारी बुलढाणा यांनी काढलेले आदेश क्र. ${propRes.data.section_21_1_notification || ""} नुसार ${propRes.data.aquired_area || ""} साठी संपादीत करुन शासना‌द्वारे ताब्यात घेण्याचे ठरविले आहे.

यादवारे या भूमीत हितसंबंधित असलेल्या सर्व व्यकर्तीना भूमीमधील त्यांच्या हितसंबंधाचे स्वरुप आणि त्यासंबंधित व्यक्तींना नुकसान भरपाईचे आणि पुनर्वसनाचे व पुनर्वसाहतीचे दावे सांगितल्या जाणाऱ्या दाव्याची रक्कम व तपशिल आणि अधिनियमाच्या कलम 20 नुसार त्यांचे कोणतेही आक्षेप असल्यास ते या बाबत निवेदन करण्यासाठी ${formData.section_21_1_representative_address || propRes.data.section_21_1_representative_address} येथे दिनांक ${propRes.data.section_21_1_submission_date} पर्यंत व्यक्तीशः किंवा आपल्या अभिकर्त्याद्वारा पुराव्यासह उपस्थित होण्यास फर्मविण्यात येत आहे.
          

सहपत्रः- संपादन क्षेत्राची यादी`;

        setDefaultMainContent(content);

        let counter = 1;

        setFormData({
          email: propRes.data.section_11_email || "",
          contactNo: propRes.data.section_11_mobile_no || "",
          caseNumber: propRes.data.section_11_bhusampadan_no || " ",
          village: propRes.data.village_name || "",
          taluka: propRes.data.taluka_name || "",
          district: propRes.data.dist_name || "",
          sectioncontent: propRes.data.section_21_1_content || content,
          header: propRes.data.section_21_1_header || "उपजिल्हाधिकारी भूसंपादन (इ.व.द.), जिल्हाधिकारी कार्यालय, बुलढाणा यांचे मार्फत कलम 21(1) नुसार काढलेली जाहिर नोटीस",
          officename: propRes.data.section_21_1_office_name || propRes.data.section_11_office_name || "उपजिल्हाधिकारी भूसंपादन (इमारत व दळणवळण)",
          branchname: propRes.data.section_21_1_branch_name || propRes.data.section_11_branch_name || "जिल्हाधिकारी कार्यालय, बुलढाणा",
          section_21_1_date: propRes.data.section_21_1_date ? new Date(propRes.data.section_21_1_date).toISOString().split("T")[0] : "",
          area: propRes.data.total_area || "",
          acquired_area: propRes.data.acquired_area || "",
          section_21_1_notification: propRes.data.section_21_1_notification || propRes.data.section_19_notification || "",
          lastDate: propRes.data.section_21_1_date ? new Date(propRes.data.section_21_1_date).toLocaleDateString('en-GB').replace(/\//g, '/') : "",
          section_21_1_representative_address: propRes.data.section_21_1_representative_address,
          section_21_1_submission_date: propRes.data.section_21_1_submission_date
            ? new Date(propRes.data.section_21_1_submission_date).toISOString().split("T")[0]
            : "",

          // MODIFIED: Inject village, taluka, and district properties into each record
          landOwners: landData.flatMap((land, landIndex) =>
            (land.owners || []).map((owner, ownerIndex) => ({
              serialNo: counter++,
              groupNo: land.survey_no || land.landacid || "",
              ownerName: owner.owner_name || land.landholder_name || "",
              areaHectare: (parseFloat(owner.owner_irrigable_area) || 0).toFixed(2),
              poHectare: (parseFloat(owner.owner_non_irrigable_area) || 0).toFixed(2),
              totalHectare: (
                (parseFloat(owner.owner_irrigable_area) || 0) +
                (parseFloat(owner.owner_non_irrigable_area) || 0)
              ).toFixed(2),
              village: land.village_name || "",
              taluka: land.taluka_name || "",
              district: land.dist_name || ""
            }))
          )

        });
      } catch (err) {
        // setError('Failed to fetch data: ' + err.message);
      } finally {
        setLoadingRecords(false);
      }
    };

    fetchAll();
    fetchusersLAO();
  }, [currentProjectId, currentProposalId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // fetch LAO users
  const fetchusersLAO = async () => {
    const role = 3;
    setLoadingusersLAO(true);
    try {
      const response = await apiService.getAssignLAO(role, currentProjectId, currentProposalId);
      if (response.status === 200 && response.data) {
        setusersLAO(Array.isArray(response.data) ? response.data : []);
      } else {
        setusersLAO([]);
      }
    } catch (error) {
      console.error('Error fetching LAO users:', error);
      setusersLAO([]);
    } finally {
      setLoadingusersLAO(false);
    }
  };

  const handleInputChange = (eOrName, value) => {
    if (typeof eOrName === "string") {
      const name = eOrName;
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      const { name, value } = eOrName.target;
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Auto-update sectioncontent when representative address or submission date changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      sectioncontent: `यादवारे नोटीस देण्यात येते की, भूसंपादन प्रकरण ${prev.caseNumber} नुसार मौजे ${prev.village} या गावामध्ये सोबतच्या यादीमध्ये नमुद असलेली तसेच अलीकडे चिन्हांकित केलेली व मोजलेली सुमारे ${prev.area} भूमी, तीच्यावर कोणत्याही इमारती बांधकाम व झाडे असल्यास त्यासह समुचित शासन म्हणून जिल्हाधिकारी बुलढाणा यांनी काढलेले आदेश क्र. ${prev.section_21_1_notification || ""} नुसार ${prev.area} साठी संपादीत करुन शासना‌द्वारे ताब्यात घेण्याचे ठरविले आहे.

यादवारे या भूमीत हितसंबंधित असलेल्या सर्व व्यकर्तीना भूमीमधील त्यांच्या हितसंबंधाचे स्वरुप आणि त्यासंबंधित व्यक्तींना नुकसान भरपाईचे आणि पुनर्वसनाचे व पुनर्वसाहतीचे दावे सांगितल्या जाणाऱ्या दाव्याची रक्कम व तपशिल आणि अधिनियमाच्या कलम 20 नुसार त्यांचे कोणतेही आक्षेप असल्यास ते या बाबत निवेदन करण्यासाठी ${prev.section_21_1_representative_address || ""} येथे दिनांक ${prev.section_21_1_submission_date ? new Date(prev.section_21_1_submission_date).toLocaleDateString('en-GB') : ""} पर्यंत व्यक्तीशः किंवा आपल्या अभिकर्त्याद्वारा पुराव्यासह उपस्थित होण्यास फर्मविण्यात येत आहे.

सहपत्रः- संपादन क्षेत्राची यादी`
    }));
  }, [formData.section_21_1_representative_address, formData.section_21_1_submission_date]);

  const handleLandOwnerChange = (index, field, value) => {
    const newLandOwners = [...formData.landOwners];
    newLandOwners[index] = {
      ...newLandOwners[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      landOwners: newLandOwners
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {
        section_21_1_bhusampadan_no: formData.fileNumber,
        section_21_1_content: formData.sectioncontent,
        section_21_1_branch_name: formData.branchname,
        section_21_1_office_name: formData.officename,
        section_21_1_header: formData.header,
        section_21_1_date: (formData.section_21_1_date),
        section_21_1_notification: formData.section_21_1_notification,
        section_21_1_submission_date: (formData.section_21_1_submission_date),
        section_21_1_representative_address: formData.section_21_1_representative_address,
      };

      await apiService.updateProposalPublish(currentProposalId, updateData);
      alert('Data updated successfully');
      setShowEditModal(false);
      setIsEditing(false);
    } catch (err) {
      setError('Failed to update data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedSignDocument) {
      const postSignedPdf = async () => {
        try {
          if (!currentProjectId || !currentProposalId) {
            console.error('Missing projectId or proposalId');
            return;
          }

          await apiService.postAddSignaturePDF(
            currentProjectId,
            currentProposalId,
            "section21",
            selectedSignDocument
          );

          const sectionRes = await apiService.getSectionFile(
            currentProjectId,
            currentProposalId,
            "section21"
          );

          if (sectionRes?.isSignAvailable && sectionRes.data?.[0]?.file_path) {
            setPreviewUrl(sectionRes.data[0].file_path);
          }
        } catch (error) {
          console.error('Error posting signed PDF:', error);
        }
      };

      postSignedPdf();
    }
  }, [selectedSignDocument]);

  const handleViewSignedPdf = async () => {
    try {
      if (!currentProjectId || !currentProposalId) {
        alert("Project or Proposal not found");
        return;
      }

      const res = await apiService.getSectionFile(
        currentProjectId,
        currentProposalId,
        "section21"
      );

      if (res?.isSignAvailable && res?.data?.[0]?.file_path) {
        setViewerFile(res.data[0].file_path);
        setIsViewerOpen(true);
      } else {
        alert("Digital signature PDF not available");
      }
    } catch (error) {
      console.error("Error opening signed PDF:", error);
      alert("Unable to open document");
    }
  };

  const handleUploadedFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPdfForSigning(file);
    setShowSignaturePad(true);
  };

  const handlePrint = () => {
    setShowPreview(true);
    setTimeout(() => {
      window.print();
      setShowPreview(false);
    }, 200);
  };

  const renderEditModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">{translations[language].edit} {translations[language].section21_1}</h2>
            <button
              onClick={() => setShowEditModal(false)}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            >
              ×
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <div className="mb-6">
                <div className="flex items-center justify-center mb-6">
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-full">
                    <h2 className="font-bold text-lg">Proposal Details</h2>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="group">
                    <label className="block font-semibold text-gray-700 mb-2 text-base">
                      प्रकरण क्रमांक (Case Number):
                    </label>
                    <input
                      type="text"
                      name="caseNumber"
                      value={formData.caseNumber}
                      onChange={(e) => handleInputChange('caseNumber', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 group-hover:border-gray-300"
                      placeholder="Enter case number..."
                    />
                  </div>

                  <div className="group">
                    <label className="block font-semibold text-gray-700 mb-2 text-base">
                      दिनांक (Date):
                    </label>
                    <input
                      type="date"
                      name="section_21_1_date"
                      value={formData.section_21_1_date || ""}
                      onChange={(e) => handleInputChange('section_21_1_date', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 group-hover:border-gray-300"
                    />
                  </div>

                  <div className="group">
                    <label className="block font-semibold text-gray-700 mb-2 text-base">
                      कार्यविवरण क्रमांक :
                    </label>
                    <input
                      type="text"
                      name="section_21_1_notification"
                      value={formData.section_21_1_notification}
                      onChange={(e) => handleInputChange('section_21_1_notification', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 group-hover:border-gray-300"
                      placeholder="Enter order number..."
                    />
                  </div>

                  <div>
                    <label className="block font-semibold text-gray-700 mb-2 text-base">
                      दावा दाखल करण्याचे ठिकाण :
                    </label>
                    <input
                      type="text"
                      name="section_21_1_representative_address"
                      value={formData.section_21_1_representative_address}
                      onChange={(e) => handleInputChange('section_21_1_representative_address', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 group-hover:border-gray-300"
                      placeholder="निवेदन करण्यासाठी..."
                    />
                  </div>

                  <div className="group">
                    <label className="block font-semibold text-gray-700 mb-2 text-base">
                      दावा दाखल करण्याची तारीख :
                    </label>
                    <input
                      type="date"
                      name="section_21_1_submission_date"
                      value={formData.section_21_1_submission_date || ""}
                      onChange={(e) => handleInputChange("section_21_1_submission_date", e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-center mb-6">
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-full">
                    <h2 className="font-bold text-lg">Main Content</h2>
                  </div>
                </div>
                <textarea
                  name="sectioncontent"
                  value={
                    formData.sectioncontent
                      ? formData.sectioncontent.replace(
                        /काढलेले\s+नुसार/,
                        `काढलेले ${formData.section_21_1_notification || ""} नुसार`
                      )
                      : defaultMainContent
                  }
                  onChange={(e) => handleInputChange('sectioncontent', e.target.value)}
                  rows={12}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter main notice content..."
                />
              </div>

              <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 transform hover:scale-105"
                >
                  {translations[language]?.Cancel}
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 flex items-center justify-center space-x-3 shadow-lg"
                >
                  {loading && (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  )}
                  <span>{loading ? 'Saving...' : translations[language].saveChanges}</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const DocumentContent = ({ isEditable = false }) => {

    // MODIFIED: Group land owners based on their specific village, taluka, and district
    const groupedLandOwners = formData.landOwners.reduce((acc, owner) => {
      const key = `${owner.village}_${owner.taluka}_${owner.district}`;
      if (!acc[key]) {
        acc[key] = {
          village: owner.village || formData.village,
          taluka: owner.taluka || formData.taluka,
          district: owner.district || formData.district,
          owners: []
        };
      }
      acc[key].owners.push(owner);
      return acc;
    }, {});

    return (
      <div className={showPreview ? 'print-content' : ''}>
        {/* Header */}
        <div className="text-center mb-5 mt-5">
          <img
            src="/Emblem_of_India.svg"
            alt="महाराष्ट्र शासन"
            className="mx-auto mb-2 w-20"
          />
          <div className="font-bold text-base">महाराष्ट्र शासन</div>
          <div className="font-bold text-base">महसूल व वन विभाग</div>
          <div className="font-bold text-2xl font-semibold">
            {formData.officename}
          </div>
          <div className="font-medium text-sm font-semibold">
            शाखा :- {formData.branchname}
          </div>
          <div className="text-sm font-semibold">
            {formData.address}
          </div>
        </div>

        <div className="text-xs mt-4">
          <div className="flex justify-between">
            <div>
              ई-मेल :- <span className="font-bold text-black-700"> {formData.email} </span>
            </div>
            <div>
              दूरध्वनी क्रमांक :-  {formData.contactNo}
            </div>
          </div>
          <hr className="border-black border-2 my-1" />
          <div className="flex justify-between">
            <div>
              {formData.section_21_1_notification}
            </div>
            <div>
              दिनांक :-  {formatDate(formData.section_21_1_date)}
            </div>
          </div>
        </div>

        {/* Page 1 */}
        <div className="page bg-white p-8">
          <div className="text-center mb-8">
            <h1 className="text-sm font-bold mb-6 underline decoration-2">भूमिसंपादन, पुनर्वसन व पुनर्वसाहत करताना उचित भरपाई मिळण्याचा व पारदर्शकतेचा हक्क अधिनियम-2013 चे कलम 21(1) नुसार जाहीर नोटीस</h1>
          </div>

          <div className="text-justify leading-relaxed space-y-4">
            {isEditable ? (
              <textarea
                defaultValue={formData.sectioncontent}
                onBlur={(e) => handleInputChange('sectioncontent', e.target.value)}
                className="w-full h-96 p-4 border-2 border-gray-300 rounded-md resize-y bg-transparent text-justify leading-relaxed"
                placeholder="Enter main content here..."
                style={{ fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
              />
            ) : (
              <div className="whitespace-pre-wrap">
                {formData.sectioncontent.includes(formData.section_21_1_notification)
                  ? formData.sectioncontent
                  : formData.sectioncontent.replace(
                    /काढलेले\s+नुसार/,
                    `काढलेले ${formData.section_21_1_notification || ""} नुसार`
                  )
                }
                <div className="mt-12">
                  <div className="flex items-end">
                    <div className="ml-auto text-right">
                      <p>({laoName || ''})</p>
                      <p className="mb-2">{laoDesi || ''}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Page 2 */}
        <div className="page bg-white p-8">
          <div className="text-center p-2 mb-2">
            <p className="font-semibold text-lg">
              भूसंपादन प्रकरण क्रमांक :{" "}
              {isEditable ? (
                <input
                  type="text"
                  defaultValue={proposalData?.section_11_bhusampadan_no || formData.caseNumber}
                  onBlur={(e) => handleInputChange('caseNumber', e.target.value)}
                  className="inline-block border-b border-red-600 bg-transparent text-center min-w-32"
                />
              ) : (
                proposalData?.section_11_bhusampadan_no || formData.caseNumber
              )}
            </p>
          </div>

          <div className="text-center mb-2">
            <p className="font-semibold text-lg underline decoration-2">संपादन क्षेत्राची यादी</p>
          </div>

          {/* MODIFIED: Loop over groups to display isolated tables for each village */}
          {Object.values(groupedLandOwners).map((group, groupIndex) => (
            <div key={groupIndex} className="mb-10">
              
              {/* Dynamic Sub-header for the Group */}
              <div className="text-center p-2 mb-1">
                <p className="font-semibold">
                  मौजे <span className="">{group.village}</span> |
                  ता. <span className="">{group.taluka}</span> |
                  जि. <span className="">{group.district}</span>
                </p>
              </div>

              <div className="print-table-container">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-black p-2 w-12">अ.क्र</th>
                      <th className="border border-black p-2 w-20">गट नं.</th>
                      <th className="border border-black p-2">भूधारकाचे नाव</th>
                      <th className="border border-black p-2 w-24">लागवड योग्य क्षेत्र (हे.आर)</th>
                      <th className="border border-black p-2 w-24">पो.ख. क्षेत्र (हे.आर)</th>
                      <th className="border border-black p-2 w-24">एकूण क्षेत्र (हे.आर)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.owners.map((owner, index) => (
                      <tr key={index}>
                        <td className="border border-black p-1 text-center">{owner.serialNo}</td>
                        <td className="border border-black p-1 text-center">{owner.groupNo}</td>
                        <td className="border border-black p-1 text-center">{owner.ownerName}</td>
                        <td className="border border-black p-1 text-center">{parseFloat(owner.areaHectare || 0).toFixed(2)}</td>
                        <td className="border border-black p-1 text-center">{parseFloat(owner.poHectare || 0).toFixed(2)}</td>
                        <td className="border border-black p-1 text-center">{parseFloat(owner.totalHectare || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          <div className="mt-12">
            <div className="flex items-end">
              <div className="ml-auto text-right">
                <p>({laoName || ''})</p>
                <p className="mb-2">{laoDesi || ''}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loadingRecords) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex justify-center items-center h-80">
          <div className="text-center animate-float">
            <div className="inline-flex relative">
              <div className="w-16 h-16 rounded-full border-4 border-slate-700 border-t-transparent animate-spin"></div>
              <div className="w-16 h-16 rounded-full border-4 border-purple-500 border-b-transparent border-r-transparent animate-spin absolute inset-0" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <FileText className="h-6 w-6 text-purple-400 animate-pulse" />
              </div>
            </div>
            <p className="text-gray-400 mt-4">कलम 21(1)(1) लोड करत आहे...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {error && <p className="text-red-600 mb-4">{error}</p>}

      {/* Action Buttons */}
      <div className="mb-6 flex flex-wrap gap-4">
        <button
          onClick={handlePrint}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold shadow-md transition-colors flex items-center gap-2"
        >
          <Printer size={18} />
          {translations[language]?.Print || 'Print'}
        </button>

        {(userRole === ROLES.LAO || userRole === ROLES.DATA_ENTRY) && (
          <button
            onClick={() => setShowEditModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-md transition-colors"
          >
            {translations[language]?.edit || 'Edit'}
          </button>
        )}
      </div>

      {/* Always show the HTML version on main screen */}
      <div className="bg-white  overflow-hidden">
        <DocumentContent isEditable={false} />
      </div>

      {/* Edit Modal */}
      {showEditModal && renderEditModal()}

      {/* Signature Pad */}
      {showSignaturePad && pdfForSigning && (
        <SignaturePad
          pdfFile={pdfForSigning}
          onSign={(signaturesArray) => {
            setShowSignaturePad(false);
            signDocument(pdfForSigning, signaturesArray)
              .then(() => {
                setPdfForSigning(null);
              })
              .catch(err => {
                console.error("Signing failed", err);
                alert("Error during digital signing");
              });
          }}
          onCancel={() => {
            setShowSignaturePad(false);
            setPdfForSigning(null);
          }}
        />
      )}

      {/* Viewer Modal */}
      {isViewerOpen && viewerFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-[90vw] max-w-6xl h-[90vh] flex flex-col shadow-2xl overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b bg-gray-50">
              <h3 className="text-xl font-semibold text-gray-800">Signed Section 21(1) Document</h3>
              <button
                onClick={() => setIsViewerOpen(false)}
                className="text-3xl font-bold text-gray-600 hover:text-gray-900 transition-colors"
              >
                ×
              </button>
            </div>
            <iframe
              src={viewerFile}
              title="Signed Section 21(1) PDF"
              className="flex-1 w-full border-0"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {/* Print styles */}
      <style jsx>{`
      @media print {
        body * { visibility: hidden; }
        .print-content, .print-content * { visibility: visible; }
        .print-content {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
        }
        @page {
          size: A4 portrait;
          margin: 0.19in 0.5in 0.25in 0.81in;
        }
        .page {
          page-break-after: always;
        }
        .page:last-child {
          page-break-after: auto;
        }
        table { border-collapse: collapse; }
      }
    `}</style>
    </div>
  );
}
export default Section21_1;